import { InjectionToken } from '@angular/core';
import { environment } from '@ups/xplat/core';
import {
  describeRynReplayListFailure,
  describeRynReplayListNoToken,
} from './ryn-replay-read-failure';
import { RynReplayAccessToken } from './ryn-replay-access-token';

/**
 * One row of `GET /api/sessions`, shaped exactly as the read API sends it.
 *
 * Field names match the server's hand-written projection in `SessionsFunction`
 * on purpose. If you rename one here to read nicer, the mapping silently yields
 * undefined and the column goes blank with no error.
 */
export interface IRynReplaySessionSummary {
  sessionId: string;
  app: string;
  platform: string;
  /** ISO 8601, UTC. */
  startedAt: string;
  /** ISO 8601, UTC. */
  lastActivityAt: string;
  /**
   * A FLOOR, not a total. Read `formatRynReplayEventFloor` before you render it.
   */
  eventCountLowerBound: number;
  errorCount: number;
  landingUrl?: string | null;
  /**
   * Who was using the app, as a Software Security user id.
   *
   * Null or absent means nobody was signed in for the part of the session we
   * saw, which is a real state and not a failure. A login page is exactly this.
   *
   * The client asserted this and the server could not check it, so it is a way to
   * FIND a recording and not evidence of who did anything. Do not put it in front
   * of a user as an accusation, and never use it to decide who may watch a
   * recording. See ReplayEventEnvelope.SecurityUserId for the mechanism.
   */
  securityUserId?: string | null;
  /** Distinct pages from the session's page trail (`RynReplaySessionPage`). */
  pageCount?: number;
  /**
   * Display name resolved at read time from Software Security (may be absent
   * when the lookup degraded or the Guid is unknown).
   */
  userName?: string | null;
  /** Email resolved at read time from Software Security. */
  email?: string | null;
  /** App version label captured at ingest (may be absent on older sessions). */
  appVersion?: string | null;
  /** Build number captured at ingest (may be absent on older sessions). */
  buildNumber?: string | null;
  /** Verbatim user-agent (#30211); absent on older sessions. */
  userAgent?: string | null;
  /** Deploy environment name (#30211); absent on older sessions. */
  environment?: string | null;
}

/**
 * The session-list filters (#30072), mirroring the server's query contract in
 * `SessionsFunction` one-to-one. Names match the wire parameter names so the
 * mapping in the HTTP source stays mechanical.
 */
export interface IRynReplaySessionFilters {
  /** Narrow to one application slug. */
  app?: string;
  /** Row cap sent to the server, which clamps it again on its own terms. */
  take?: number;
  /** Software Security user id (Guid). */
  user?: string;
  /** Name or email search resolved server-side (#31323). */
  userSearch?: string;
  /** ISO 8601 UTC, inclusive lower bound on the session start. */
  from?: string;
  /** ISO 8601 UTC, EXCLUSIVE upper bound on the session start. */
  to?: string;
  /** True keeps only sessions that recorded errors. False/absent means all. */
  hasErrors?: boolean;
  /** Contains-match on the landing URL. */
  landingUrl?: string;
  /** Contains-match on ANY page in the session's page trail. */
  visitedUrl?: string;
  /** Contains-match on AppVersion or BuildNumber. */
  appVersion?: string;
  /** Exact match on Environment (#30211). */
  environment?: string;
  /** Browser family token: chrome | edge | firefox | safari (#30211). */
  browser?: string;
  /** Contains-match on ANY captured console message in the session (#31322). */
  consoleLog?: string;
}

/**
 * Aggregates over the WHOLE filtered set, as the server computes them — not
 * over the page of rows received, which is TOP-clamped. See
 * `ReplaySessionListStats` server-side for why the distinction matters and why
 * `identifiedUserCount` excludes anonymous sessions.
 */
export interface IRynReplaySessionListStats {
  sessionCount: number;
  identifiedUserCount: number;
  /** Null when the filtered set is empty — an average of nothing is not 0s. */
  avgDurationSeconds: number | null;
}

/**
 * What a list request answers: the rows, plus the whole-set stats when the
 * server supplied them. `stats` is null (never fabricated zeros) when the
 * server did not send any — a stat bar must render "unknown", not "0 users".
 */
export interface IRynReplaySessionListResult {
  sessions: IRynReplaySessionSummary[];
  stats: IRynReplaySessionListStats | null;
}

/**
 * Where the session list gets its rows.
 *
 * An interface for the same reason the event source is one. The normal path is
 * the gated read API, but a support engineer holding a folder of `.ndjson`
 * exports has a real list too, and neither is a test double.
 */
export interface IRynReplaySessionSource {
  list(
    filters?: IRynReplaySessionFilters
  ): Promise<IRynReplaySessionListResult>;
}

export const RYN_REPLAY_SESSION_SOURCE =
  new InjectionToken<IRynReplaySessionSource>('RYN_REPLAY_SESSION_SOURCE');

/**
 * Which action, if any, resolves a read refusal (#31381).
 *
 *   - `entra-sign-in-required`: no Entra token, so a corporate Entra sign-in is
 *     the fix. This is the one refusal the surface offers a sign-in button for.
 *   - `generic`: everything else (not configured, 401/403/503, bad shape). A
 *     login does not fix these, so no button is offered.
 */
export type RynReplayReadRefusalKind = 'generic' | 'entra-sign-in-required';

/** Thrown with a message already fit to show a user. */
export class RynReplayListError extends Error {
  constructor(
    message: string,
    readonly kind: RynReplayReadRefusalKind = 'generic'
  ) {
    super(message);
  }
}

/**
 * Renders the event count honestly.
 *
 * The server stores this as a MAX across batches, never a SUM, because the
 * ingest envelope carries no batch identity and an additive merge would inflate
 * unrecoverably on retry. A session of three 50-event batches therefore stores
 * 50, not 150.
 *
 * So the number is a floor and it must look like one. Printing a bare "50" in a
 * column headed Events states something false with total confidence, and every
 * person reading the screen would take it as the total. The server went to the
 * trouble of naming the field `eventCountLowerBound` to stop exactly that, and
 * the UI is the last place it can still go wrong.
 *
 * Zero is the one value that is not a floor worth marking. A session with no
 * events recorded has no more hiding behind it, and "0+" reads like a defect.
 */
export const formatRynReplayEventFloor = (count: number): string => {
  if (!Number.isFinite(count) || count <= 0) {
    return '0';
  }
  return `${Math.floor(count)}+`;
};

/**
 * Human-readable app version for list and player chrome (#30545).
 *
 * Missing values render as "Unknown" rather than blank or an error, because
 * sessions recorded before version capture landed have no stored label.
 */
export const formatRynReplayAppVersionLabel = (
  appVersion?: string | null,
  buildNumber?: string | null
): string => {
  const version = appVersion?.trim();
  const build = buildNumber?.trim();
  if (version && build) {
    return `${version} (${build})`;
  }
  if (version) {
    return version;
  }
  if (build) {
    return build;
  }
  return 'Unknown';
};

/**
 * Reads `GET {readApiUrl}/sessions`.
 *
 * The viewer's token is supplied by the host app rather than acquired here, the
 * same arrangement the event source uses and for the same reason: apps in this
 * monorepo differ on whether the bearer comes from Auth0 or Entra, and this
 * class should not be guessing which one it is holding.
 */
export class RynReplayHttpSessionSource implements IRynReplaySessionSource {
  constructor(
    private readonly accessToken: () => Promise<RynReplayAccessToken>,
    private readonly fetchRef: typeof fetch = globalThis.fetch?.bind(globalThis)
  ) {}

  async list(
    filters: IRynReplaySessionFilters = {}
  ): Promise<IRynReplaySessionListResult> {
    const base = environment.rynReplay?.readApiUrl?.replace(/\/+$/, '');
    if (!base) {
      throw new RynReplayListError(
        'The replay read API is not configured for this app, so recordings cannot be listed.'
      );
    }

    const access = await this.accessToken();
    if (!access.token) {
      // No token is three states, not one (#30126), and the factory now says
      // which: nobody signed in, a session on an issuer the read API rejects
      // (Auth0), or a failed silent acquisition. Wording the refusal from the
      // reason points an Auth0-signed-in viewer at their corporate sign in
      // instead of telling them their login is broken, which it is not.
      // #31381: every no-token refusal is one a corporate Entra sign-in can fix,
      // so it keeps the entra-sign-in-required kind that drives the sign-in
      // button; only the wording now varies by reason.
      throw new RynReplayListError(
        describeRynReplayListNoToken(access.reason),
        'entra-sign-in-required'
      );
    }
    const token = access.token;

    // Built with URLSearchParams rather than string concatenation so a filter
    // containing a reserved character cannot smuggle a second parameter in.
    // Unset filters stay OFF the wire: the server treats an absent parameter as
    // "no narrowing" but refuses some present-and-empty ones.
    const query = new URLSearchParams();
    if (filters.app) {
      query.set('app', filters.app);
    }
    if (filters.take && filters.take > 0) {
      query.set('take', String(Math.floor(filters.take)));
    }
    if (filters.user) {
      query.set('user', filters.user);
    }
    if (filters.userSearch) {
      query.set('userSearch', filters.userSearch);
    }
    if (filters.from) {
      query.set('from', filters.from);
    }
    if (filters.to) {
      query.set('to', filters.to);
    }
    if (filters.hasErrors) {
      query.set('hasErrors', 'true');
    }
    if (filters.landingUrl) {
      query.set('landingUrl', filters.landingUrl);
    }
    if (filters.visitedUrl) {
      query.set('visitedUrl', filters.visitedUrl);
    }
    if (filters.appVersion) {
      query.set('appVersion', filters.appVersion);
    }
    if (filters.environment) {
      query.set('environment', filters.environment);
    }
    if (filters.browser) {
      query.set('browser', filters.browser);
    }
    if (filters.consoleLog) {
      query.set('consoleLog', filters.consoleLog);
    }
    const suffix = query.toString() ? `?${query.toString()}` : '';

    const response = await this.fetchRef(`${base}/sessions${suffix}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      const message = await readRynReplayListError(response);
      throw new RynReplayListError(message);
    }

    const body = await response.json();

    // An empty list is a legitimate answer, not a failure. A new app that has
    // recorded nothing yet, or a filter that matches nothing, both land here and
    // the screen should say "no recordings" rather than raise an error.
    const rows = body?.sessions;
    if (!Array.isArray(rows)) {
      throw new RynReplayListError(
        'The replay service returned a response this app could not read.'
      );
    }

    return {
      sessions: rows as IRynReplaySessionSummary[],
      stats: parseListStats(body?.stats),
    };
  }
}

/**
 * 401 / 403 / 503 keep their dedicated sentences even when the body has
 * `{ error }` — those statuses are the diagnosis. Other refusals (userSearch
 * 400, etc.) prefer the server's error text so support sees the real reason.
 */
const readRynReplayListError = async (response: Response): Promise<string> => {
  if (
    response.status === 401 ||
    response.status === 403 ||
    response.status === 503
  ) {
    return describeRynReplayListFailure(response.status);
  }
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body?.error === 'string' && body.error.trim()) {
      return body.error;
    }
  } catch {
    // Body was not JSON — use the status-based sentence below.
  }
  return describeRynReplayListFailure(response.status);
};

/**
 * Type the server's stats object, or answer null.
 *
 * Null, never zeros, when the server sent nothing usable: a stat bar rendering
 * fabricated `0 users / 0 sessions` over a grid that visibly has rows is the
 * confident-wrong-number shape this feature refuses everywhere else.
 */
const parseListStats = (raw: unknown): IRynReplaySessionListStats | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const stats = raw as Record<string, unknown>;
  const sessionCount = stats['sessionCount'];
  const identifiedUserCount = stats['identifiedUserCount'];
  const avgDurationSeconds = stats['avgDurationSeconds'];
  if (
    typeof sessionCount !== 'number' ||
    typeof identifiedUserCount !== 'number'
  ) {
    return null;
  }
  return {
    sessionCount,
    identifiedUserCount,
    avgDurationSeconds:
      typeof avgDurationSeconds === 'number' ? avgDurationSeconds : null,
  };
};

/**
 * Serves a list handed over in memory.
 *
 * Used by a host that already holds session metadata, and by dev verification
 * against real exports before the read API is reachable. Kept in the shipping
 * file rather than a test folder because the file event source sits alongside
 * the HTTP one for the same reason: owning your data means being able to work
 * from a copy of it.
 */
export class RynReplayStaticSessionSource implements IRynReplaySessionSource {
  constructor(private readonly rows: IRynReplaySessionSummary[]) {}

  async list(
    filters: IRynReplaySessionFilters = {}
  ): Promise<IRynReplaySessionListResult> {
    // Filter semantics deliberately mirror the server's, so a dev check against
    // this source proves something about production: user id equality, half-open
    // date window (from inclusive, to exclusive), errorCount > 0, contains-match
    // on URLs. `visitedUrl` is matched against the landing URL because summaries
    // carry no page trail — a strictly NARROWER match than the server's, which
    // fails closed rather than inventing visits.
    const filtered = this.rows.filter((r) => {
      if (filters.app && r.app !== filters.app) {
        return false;
      }
      if (filters.user && r.securityUserId !== filters.user) {
        return false;
      }
      if (filters.userSearch) {
        return false;
      }
      if (filters.hasErrors && !(r.errorCount > 0)) {
        return false;
      }
      const started = Date.parse(r.startedAt);
      if (filters.from && started < Date.parse(filters.from)) {
        return false;
      }
      if (filters.to && started >= Date.parse(filters.to)) {
        return false;
      }
      if (
        filters.landingUrl &&
        !(r.landingUrl ?? '').includes(filters.landingUrl)
      ) {
        return false;
      }
      if (
        filters.visitedUrl &&
        !(r.landingUrl ?? '').includes(filters.visitedUrl)
      ) {
        return false;
      }
      if (filters.appVersion) {
        const needle = filters.appVersion;
        const version = r.appVersion ?? '';
        const build = r.buildNumber ?? '';
        if (!version.includes(needle) && !build.includes(needle)) {
          return false;
        }
      }
      if (
        filters.environment &&
        (r.environment ?? '').toLowerCase() !==
          filters.environment.toLowerCase()
      ) {
        return false;
      }
      if (filters.browser) {
        const ua = r.userAgent ?? '';
        const family = filters.browser.toLowerCase();
        const matches =
          (family === 'chrome' &&
            /Chrome\//i.test(ua) &&
            !/Edg\//i.test(ua) &&
            !/OPR\//i.test(ua)) ||
          (family === 'edge' && /Edg\//i.test(ua)) ||
          (family === 'firefox' && /Firefox\//i.test(ua)) ||
          (family === 'safari' &&
            /Safari\//i.test(ua) &&
            !/Chrome\//i.test(ua) &&
            !/Chromium\//i.test(ua));
        if (!matches) {
          return false;
        }
      }
      if (filters.consoleLog?.trim()) {
        // Static summaries carry no console trail — fail closed rather than
        // returning the unscoped superset while the UI claims a console filter.
        return false;
      }
      return true;
    });

    // Stats over the FILTERED set, before the take clamp — same population the
    // server's aggregates describe.
    const users = new Set(
      filtered
        .map((r) => r.securityUserId)
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
    );
    const durations = filtered
      .map(
        (r) => (Date.parse(r.lastActivityAt) - Date.parse(r.startedAt)) / 1000
      )
      .filter((s) => Number.isFinite(s) && s >= 0);
    const stats: IRynReplaySessionListStats = {
      sessionCount: filtered.length,
      identifiedUserCount: users.size,
      avgDurationSeconds:
        durations.length === 0
          ? null
          : durations.reduce((a, b) => a + b, 0) / durations.length,
    };

    let out = [...filtered];
    if (filters.take && filters.take > 0) {
      out = out.slice(0, Math.floor(filters.take));
    }
    return { sessions: out, stats };
  }
}
